import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Grid, TextField, Button, MenuItem, Typography, Slider, Paper, Checkbox, FormControlLabel
} from '@mui/material';
import RecipeIngredientRow from './RecipeIngredientRow';
import GaugeMeter2 from './GaugeMeter2';
import IngredientForm from './IngredientForm';
import { useTranslation } from 'react-i18next';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Recipe } from './Recipe';
import FreezingCurve from './FreezingCurve';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import SettingsIcon from '@mui/icons-material/Settings';
import Popover from '@mui/material/Popover';
import FormGroup from '@mui/material/FormGroup';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import TranslateIcon from '@mui/icons-material/Translate';
import { useAppContext } from '../context/AppContext';
import { formatNumber, formatPercentage } from '../utils/formatNumber';
import { parseBoolean } from '../utils/boolean';
import { InclusionIcon } from './IngredientIcons';
import RecipeTranslationsDialog from './RecipeTranslationsDialog';

const marks = [
  { value: 0, label: '0°C' },
  { value: -11, label: '-11°C' },
  { value: -18, label: '-18°C' },
  { value: -30, label: '-30°C' },
];

const defaultIngredient = { id: '', amount: '', in_mix: true };

const RecipeEditor = ({ onSaved, recipe: initialRecipe, api }) => {
  const [name, setName] = useState('');
  const [servingTemp, setServingTemp] = useState(-18);
  const [target_hardness, setTargetHardness] = useState(75);
  const [overrun, setOverrun] = useState(30);
  const [evaporationPercentage, setEvaporationPercentage] = useState(0);
  const [includeAlcoholInEvaporation, setIncludeAlcoholInEvaporation] = useState(true);
  const [ingredients, setIngredients] = useState([{ ...defaultIngredient }]);
  const [notes, setNotes] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const { ingredients: ingredientOptions, categories, language, refreshIngredients } = useAppContext();
  const [copyGrams, setCopyGrams] = useState(1000);
  const [status, setStatus] = useState('draft');
  const [importWarnings, setImportWarnings] = useState([]);
  const { t } = useTranslation();
  const fileInputRef = React.useRef();
  const [hardnessCalc, setHardnessCalc] = useState(null);
  const [wfCalc, setWfCalc] = useState(null);
  const [fusionPoint, setFusionPoint] = useState(null);
  const [editingId, setEditingId] = useState(initialRecipe && initialRecipe.id ? initialRecipe.id : null);
  const [showIngredientDialog, setShowIngredientDialog] = useState(false);
  const [newIngredientData, setNewIngredientData] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [translationsDialogOpen, setTranslationsDialogOpen] = useState(false);

  // Estado para columnas visibles
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem('recipeEditorVisibleColumns');
    return saved ? JSON.parse(saved) : {
      fat: true,
      msnf: true,
      total_solids: true,
      pac: true,
      pod: true,
      sugars: false,
      lactose: false,
      water: false,
      proteins: false,
      alcohol: false
    };
  });
  const [columnSettingsAnchor, setColumnSettingsAnchor] = useState(null);
  const visibleMetricsCount = useMemo(
    () => Object.values(visibleColumns).filter(Boolean).length,
    [visibleColumns]
  );
  const tableColumnSpan = visibleMetricsCount + 4; // drag + ingredient + amount + actions

  // Estado para propiedades ajustadas visibles
  const [visibleAdjustedProps, setVisibleAdjustedProps] = useState(() => {
    const saved = localStorage.getItem('recipeEditorVisibleAdjustedProps');
    return saved ? JSON.parse(saved) : {
      total_solids: true,
      pac: true,
      fat: false,
      msnf: false,
      sugars: false,
      proteins: false
    };
  });
  const [adjustedPropsSettingsAnchor, setAdjustedPropsSettingsAnchor] = useState(null);

  // Helper para procesar ingredientes y crear temporales si es necesario
  const processRecipeIngredients = useCallback((recipeIngredients, storeTemporaryIngredients = false) => {
    const missingIngredients = [];
    const availableIngredients = recipeIngredients.map(ing => {
      const existsInList = ingredientOptions.find(opt => opt.id === ing.id);
      const option = existsInList;
      let inMix = parseBoolean(ing?.in_mix ?? ing?.pivot?.in_mix, true);
      if (option) {
        if (option.can_be_mix === false) {
          inMix = false;
        } else if (option.can_be_inclusion === false) {
          inMix = true;
        }
      }
      if (!existsInList && ing.id) {
        // Ingrediente no disponible, crear una copia temporal
        const tempIngredient = {
          ...ing, // Usar todos los datos del ingrediente que vienen con la receta
          name: `${ing.name} (${t('from_recipe') || 'de receta'})`,
          can_edit: false,
          can_delete: false,
          _isTemporary: true
        };
        missingIngredients.push(tempIngredient);
        
        // Si necesitamos guardar los ingredientes temporales para uso posterior
        if (storeTemporaryIngredients) {
          // Agregar al estado o contexto global si es necesario
          // Por ahora, los manejamos localmente
        }
        
        return { id: ing.id, amount: ing.amount, in_mix: inMix };
      }
      return { id: ing.id, amount: ing.amount, in_mix: inMix };
    });

    // Mostrar advertencia si hay ingredientes faltantes
    if (missingIngredients.length > 0) {
      setImportWarnings(prev => [
        ...prev,
        t('recipe_has_unavailable_ingredients') || 
        `Esta receta usa ${missingIngredients.length} ingrediente(s) que no están en tu lista. Se han agregado temporalmente.`
      ]);
    }

    return { 
      ingredients: availableIngredients.length > 0 ? availableIngredients : [{ ...defaultIngredient }],
      temporaryIngredients: missingIngredients
    };
  }, [ingredientOptions, t]);

  // Estado para almacenar ingredientes temporales
  const [temporaryIngredients, setTemporaryIngredients] = useState([]);

  // Helper para cargar datos básicos de receta
  const loadRecipeBasicData = useCallback((data) => {
    setName(data.name || '');
    setCategoryId(data.category_id);
    setServingTemp(data.serving_temp || -18);
    setTargetHardness(data.target_hardness || 75);
    setOverrun(data.overrun || 30);
    setEvaporationPercentage(data.evaporation_percentage || 0);
    setIncludeAlcoholInEvaporation(data.include_alcohol_in_evaporation !== false);
    setNotes(data.notes || '');
    setStatus(data.status || 'draft');
  }, []);

  // Load recipe for editing
  useEffect(() => {
    if (editingId) {
      api.getRecipe(editingId)
        .then(data => {
          loadRecipeBasicData(data);
          const result = processRecipeIngredients(data.ingredients || [], true);
          setIngredients(result.ingredients);
          setTemporaryIngredients(result.temporaryIngredients);
        });
    } else if (initialRecipe) {
      loadRecipeBasicData(initialRecipe);

      // Manejar ingredientes según el caso
      if (initialRecipe.cloneFrom) {
        // Cargar ingredientes desde la API para el clon
        api.getRecipe(initialRecipe.cloneFrom).then(data => {
          const result = processRecipeIngredients(data.ingredients || [], true);
          setIngredients(result.ingredients);
          setTemporaryIngredients(result.temporaryIngredients);
          if (data.notes) setNotes(data.notes);
        });
      } else if (initialRecipe.ingredients) {
        // Ya tiene ingredientes, procesarlos directamente
        const result = processRecipeIngredients(initialRecipe.ingredients, true);
        setIngredients(result.ingredients);
        setTemporaryIngredients(result.temporaryIngredients);
      } else {
        setIngredients([{ ...defaultIngredient }]);
        setTemporaryIngredients([]);
      }
    }
    // eslint-disable-next-line
  }, [editingId, initialRecipe, processRecipeIngredients, loadRecipeBasicData, api]);

  const selectedCategory = useMemo(
    () => categories.find(cat => cat.id === Number(categoryId)),
    [categories, categoryId]
  );

  const propertyDescriptions = useMemo(() => ({
    fat: { label: t('fat'), desc: t('desc_fat') },
    milkfat: { label: t('milkfat'), desc: t('desc_milkfat') },
    msnf: { label: t('msnf'), desc: t('desc_msnf') },
    lactose: { label: t('lactose'), desc: t('desc_lactose') },
    lactose_water_ratio: { label: t('lactose_water_ratio'), desc: t('desc_lactose_water_ratio') },
    pod: { label: t('pod'), desc: t('desc_pod') },
    pac: { label: t('pac'), desc: t('desc_pac') },
    pacn: { label: t('pacn'), desc: t('desc_pacn') },
    stabilizer: { label: t('stabilizer'), desc: t('desc_stabilizer') },
    emulsifiers: { label: t('emulsifiers'), desc: t('desc_emulsifiers') },
    total_solids: { label: t('total_solids'), desc: t('desc_total_solids') },
    water: { label: t('water'), desc: t('desc_water') },
    evaporation_percentage: { label: t('evaporation_percentage'), desc: t('desc_evaporation_percentage') },
  }), [t]);

  // Instancia de Recipe para cálculos - usar ingredientes combinados
  const combinedIngredientOptions = useMemo(() => {
    // Combinar ingredientes disponibles con ingredientes temporales
    return [...ingredientOptions, ...temporaryIngredients];
  }, [ingredientOptions, temporaryIngredients]);

  const recipeObj = useMemo(() => new Recipe({
    ingredients,
    ingredientOptions: combinedIngredientOptions, // Usar ingredientes combinados
    servingTemp,
    target_hardness,
    overrun,
    evaporationPercentage,
    includeAlcoholInEvaporation,
    notes,
    categoryId,
    name
  }), [ingredients, combinedIngredientOptions, servingTemp, target_hardness, overrun, evaporationPercentage, includeAlcoholInEvaporation, notes, categoryId, name]);

  const mixIngredientIndexes = useMemo(() => {
    console.log(ingredients);
    return ingredients.reduce((acc, ing, idx) => {
      if (parseBoolean(ing.in_mix, true)) {
        acc.push(idx);
      }
      return acc;
    }, []);
  }, [ingredients]);

  const nonMixIngredientIndexes = useMemo(() => {
    return ingredients.reduce((acc, ing, idx) => {
      if (!parseBoolean(ing.in_mix, true)) {
        acc.push(idx);
      }
      return acc;
    }, []);
  }, [ingredients]);

  const hasEmptyInclusionRow = useMemo(() => {
    return ingredients.some(ing => {
      if (parseBoolean(ing.in_mix, true)) return false;
      return !ing.id && !ing.amount;
    });
  }, [ingredients]);

  const mixWeight = recipeObj.getMixWeight();
  const inclusionWeight = recipeObj.getInclusionsWeight();

  // Calcula valores derivados cuando cambian los ingredientes o parámetros
  useEffect(() => {
    setHardnessCalc(recipeObj.getHardnessIteratingInServingTemp?.() ?? null);
    setWfCalc(recipeObj.getWfIteratingInServingTemp?.() ?? null);
    setFusionPoint(recipeObj.getFusionPoint?.() ?? null);
    // eslint-disable-next-line
  }, [recipeObj, servingTemp, target_hardness, overrun, evaporationPercentage, includeAlcoholInEvaporation, ingredients, ingredientOptions, categoryId, name]);

  // Drag and drop handlers
  const handleDragStart = idx => e => {
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", idx);
  };
  const handleDragOver = idx => e => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };
  const handleDrop = idx => e => {
    e.preventDefault();
    const fromIdx = Number(e.dataTransfer.getData("text/plain"));
    if (fromIdx === idx) return;
    const newIngredients = [...ingredients];
    const [moved] = newIngredients.splice(fromIdx, 1);
    newIngredients.splice(idx, 0, moved);
    setIngredients(newIngredients);
  };

  // Ingredient row handlers
  const handleIngredientChange = useCallback((idx, field, value) => {
    setIngredients(ings =>
      ings.map((ing, i) => {
        if (i !== idx) return ing;

        if (field === 'id') {
          const selected = combinedIngredientOptions.find(opt => opt.id === value);
          const updated = { ...ing, id: value };
          if (selected) {
            if (selected.can_be_mix === false) {
              updated.in_mix = false;
            } else if (selected.can_be_inclusion === false) {
              updated.in_mix = true;
            } else if (typeof updated.in_mix === 'undefined') {
              updated.in_mix = true;
            }
          } else {
            updated.in_mix = true;
          }
          return updated;
        }

        if (field === 'in_mix') {
          const option = combinedIngredientOptions.find(opt => opt.id === ing.id);
          if (option) {
            if (option.can_be_mix === false) {
              return { ...ing, in_mix: false };
            }
            if (option.can_be_inclusion === false) {
              return { ...ing, in_mix: true };
            }
          }
          return { ...ing, in_mix: value };
        }

        return { ...ing, [field]: value };
      })
    );
  }, [combinedIngredientOptions]);

  const removeIngredient = useCallback(idx =>
    setIngredients(ings => ings.filter((_, i) => i !== idx)), []
  );

  // Helper para crear datos de receta para guardar
  const createRecipeData = useCallback(() => ({
    name,
    'category_id': categoryId,
    'serving_temp': servingTemp,
    'target_hardness': target_hardness,
    'overrun': overrun,
    'evaporation_percentage': evaporationPercentage,
    'include_alcohol_in_evaporation': includeAlcoholInEvaporation,
    'weight': recipeObj.getTotalWeight(),
    'pod': recipeObj.getCurrentValue('pod'),
    'pac': recipeObj.getCurrentValue('pac'),
    'solids': recipeObj.getCurrentValue('total_solids'),
    'fat': recipeObj.getCurrentValue('fat'),
    'msnf': recipeObj.getCurrentValue('msnf'),
    ingredients: ingredients
      .filter(ing => ing.id && ing.amount)
      .map(ing => ({
        id: ing.id,
        amount: ing.amount,
        in_mix: parseBoolean(ing.in_mix, true)
      })),
    notes,
    status
  }), [name, categoryId, servingTemp, target_hardness, overrun, evaporationPercentage, includeAlcoholInEvaporation, recipeObj, ingredients, notes, status]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const recipe = createRecipeData();
    
    try {
      if (editingId) {
        await api.putRecipe(editingId, recipe);
      } else {
        const saved = await api.postRecipe(recipe);
        if (saved?.id) {
          setEditingId(saved.id);
          loadRecipeBasicData(saved);
          setIngredients(saved.ingredients || [{ ...defaultIngredient }]);
        }
      }
      if (onSaved) onSaved();
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  // Helper para crear datos de exportación
  const createExportData = useCallback(() => ({
    name,
    category_id: categoryId,
    serving_temp: servingTemp,
    target_hardness,
    overrun,
    evaporation_percentage: evaporationPercentage,
    include_alcohol_in_evaporation: includeAlcoholInEvaporation,
    notes,
    status,
    ingredients: ingredients.map(ing => ({
      ...ing,
      in_mix: parseBoolean(ing.in_mix, true)
    })),
  }), [name, categoryId, servingTemp, target_hardness, overrun, evaporationPercentage, includeAlcoholInEvaporation, notes, status, ingredients]);

  // Exportar receta
  const handleExport = () => {
    const exportData = createExportData();
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${name || 'recipe'}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Helper para procesar ingredientes importados
  const processImportedIngredients = useCallback((importedIngredients) => {
    const warnings = [];
    
    importedIngredients.forEach(ing => {
      if (!ing.id) return;
      const current = ingredientOptions.find(opt => opt.id === ing.id);
      if (current) {
        // Compara propiedades relevantes
        const keysToCompare = Object.keys(ing).filter(
          k => !['created_at', 'updated_at', 'translations', 'amount', 'id', 'pivot', 'in_mix'].includes(k)
        );
        keysToCompare.forEach(key => {
          if (
            typeof ing[key] !== 'undefined' &&
            typeof current[key] !== 'undefined' &&
            String(ing[key]) !== String(current[key])
          ) {
            warnings.push(
              `${t('ingredient')}: "${current.name}" - ${key}: ${ing[key]} → ${current[key]}`
            );
          }
        });
      }
    });
    
    return warnings;
  }, [ingredientOptions, t]);

  // Importar receta
  const handleImport = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = evt => {
      try {
        const data = JSON.parse(evt.target.result);
        loadRecipeBasicData(data);

        if (Array.isArray(data.ingredients)) {
          const warnings = processImportedIngredients(data.ingredients);
          setImportWarnings(warnings);
          const mappedIngredients = data.ingredients.map(ing => {
            const option = ingredientOptions.find(opt => opt.id === ing.id);
            let inMix = parseBoolean(ing.in_mix, true);
            if (option) {
              if (option.can_be_mix === false) {
                inMix = false;
              } else if (option.can_be_inclusion === false) {
                inMix = true;
              }
            }
            return { ...ing, in_mix: inMix };
          });
          setIngredients(mappedIngredients);
        } else {
          setIngredients([{ ...defaultIngredient }]);
          setImportWarnings([]);
        }
      } catch (err) {
        alert('Error importing recipe: ' + err.message);
        setImportWarnings([]);
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  // Helper para crear datos de ingrediente desde receta
  const createIngredientFromRecipe = useCallback(() => ({
    name: name || 'Recipe Ingredient',
    category: 'Other',
    sugars: formatNumber(recipeObj.getAdjustedValue('sugars') || 0),
    fats: formatNumber(recipeObj.getAdjustedValue('fat') || 0),
    milkfat: formatNumber(recipeObj.getAdjustedValue('milkfat') || 0),
    proteins: formatNumber(recipeObj.getAdjustedValue('proteins') || 0),
    other_solids: formatNumber(recipeObj.getAdjustedValue('other_solids') || 0),
    total_solids: formatNumber(recipeObj.getAdjustedValue('total_solids') || 0),
    water: formatNumber(recipeObj.getAdjustedValue('water') || 0),
    pac: formatNumber(recipeObj.getAdjustedValue('pac') || 0),
    pod: formatNumber(recipeObj.getAdjustedValue('pod') || 0),
    msnf: formatNumber(recipeObj.getAdjustedValue('msnf') || 0),
    lactose: formatNumber(recipeObj.getAdjustedValue('lactose') || 0),
    alcohol: formatNumber(recipeObj.getAdjustedValue('alcohol') || 0),
    salt: formatNumber(recipeObj.getAdjustedValue('salt') || 0),
    stabilizer: formatNumber(recipeObj.getAdjustedValue('stabilizer') || 0),
    emulsifiers: formatNumber(recipeObj.getAdjustedValue('emulsifiers') || 0),
    hardening_factor: formatNumber(recipeObj.getAdjustedValue('hardening_factor') || 0)
  }), [name, recipeObj]);

  // Crear ingrediente a partir de la receta
  const handleCreateIngredient = () => {
    const recipeData = createIngredientFromRecipe();
    setNewIngredientData(recipeData);
    setShowIngredientDialog(true);
  };

  const handleIngredientDialogClose = () => {
    setShowIngredientDialog(false);
    setNewIngredientData(null);
  };

  const handleIngredientSaved = () => {
    setShowIngredientDialog(false);
    setNewIngredientData(null);
    // Refresh ingredient options after saving new ingredient
    refreshIngredients();
  };

  // statusOptions debe estar dentro del componente para acceder a t
  const statusOptions = [
    { value: 'publish', label: t('publish') },
    { value: 'private', label: t('private') },
    { value: 'draft', label: t('draft') },
    { value: 'trash', label: t('trash') }
  ];

  // Calcula color para la dureza
  let hardnessDiff = null;
  let hardnessColor = undefined;
  if (hardnessCalc !== null && typeof hardnessCalc === 'number') {
    hardnessDiff = Math.abs(hardnessCalc - target_hardness);
    if (hardnessDiff > 5) {
      hardnessColor = '#d32f2f'; // red
    } else if (hardnessDiff > 2) {
      hardnessColor = '#c48800'; // yellow
    } else {
      hardnessColor = '#388e3c'; // green
    }
  }

  useEffect(() => {
    const hasEmptyMixRow = ingredients.some(ing => {
      return parseBoolean(ing.in_mix, true) && !ing.id  && !ing.amount;
    });
    if (!hasEmptyMixRow) {
      setIngredients(ings => [...ings, { ...defaultIngredient }]);
    }
  }, [ingredients]);

  useEffect(() => {
    if (hasEmptyInclusionRow) return;
    setIngredients(prev => {
      const alreadyHasEmpty = prev.some(ing => {
        return !parseBoolean(ing.in_mix, true) && !ing.id && !ing.amount;
      });
      if (alreadyHasEmpty) return prev;
      return [...prev, { ...defaultIngredient, in_mix: false }];
    });
  }, [hasEmptyInclusionRow]);

  const handleColumnToggle = (column) => {
    const newVisibleColumns = { ...visibleColumns, [column]: !visibleColumns[column] };
    setVisibleColumns(newVisibleColumns);
    localStorage.setItem('recipeEditorVisibleColumns', JSON.stringify(newVisibleColumns));
  };

  const availableColumns = [
    { key: 'fat', label: t('fat') },
    { key: 'msnf', label: t('msnf') },
    { key: 'total_solids', label: t('total_solids') },
    { key: 'pac', label: t('pac') },
    { key: 'pod', label: t('pod') },
    { key: 'sugars', label: t('sugars') },
    { key: 'lactose', label: t('lactose') },
    { key: 'water', label: t('water') },
    { key: 'proteins', label: t('proteins') },
    { key: 'alcohol', label: t('alcohol') }
  ];

  const handleAdjustedPropToggle = (prop) => {
    const newVisibleProps = { ...visibleAdjustedProps, [prop]: !visibleAdjustedProps[prop] };
    setVisibleAdjustedProps(newVisibleProps);
    localStorage.setItem('recipeEditorVisibleAdjustedProps', JSON.stringify(newVisibleProps));
  };

  const availableAdjustedProps = [
    { key: 'total_solids', label: t('total_solids') },
    { key: 'pac', label: t('pac') },
    { key: 'fat', label: t('fat') },
    { key: 'msnf', label: t('msnf') },
    { key: 'sugars', label: t('sugars') },
    { key: 'proteins', label: t('proteins') },
    { key: 'lactose', label: t('lactose') },
    { key: 'pod', label: t('pod') },
  ];

  // Render
  return (
    <form onSubmit={handleSubmit}>
      <Grid container spacing={2}>
        <Grid item size={{ xs: 12, sm: 6 }}>
          <TextField label={t('name')} value={name} onChange={e => setName(e.target.value)} required fullWidth />
        </Grid>
        <Grid item size={{ xs: 12, sm: 6 }}>
          <TextField select label={t('category')}
            value={categoryId}
            onChange={e => setCategoryId(e.target.value)}
            fullWidth>
            {categories.map(tcat => <MenuItem key={tcat.id} value={tcat.id}>
            {tcat.translations && tcat.translations[language] && tcat.translations[language].name || tcat.name}</MenuItem>)}
          </TextField>
        </Grid>
        <Grid item size={{ xs: 12, sm: 6 }}>
          <Typography gutterBottom>
            {t('serving_temp')} ({servingTemp}°C)
          </Typography>
          <Slider min={-35} max={-5}
            value={servingTemp}
            onChange={(_, v) => setServingTemp(v)}
            valueLabelDisplay="auto"
            marks={marks}
          />
        </Grid>
        <Grid item size={{ xs: 12, sm: 3 }}>
          <Typography gutterBottom>{t('hardness')} ({target_hardness}%)</Typography>
          <Slider min={55} max={90} value={target_hardness}
            onChange={(_, v) => setTargetHardness(v)}
            valueLabelDisplay="auto" />
        </Grid>
        <Grid item size={{ xs: 12, sm: 3 }}>
          <Typography gutterBottom>{t('overrun')} ({overrun}%)</Typography>
          <Slider min={0} max={150} value={overrun}
            onChange={(_, v) => setOverrun(v)}
            valueLabelDisplay="auto" />
        </Grid>
        <Grid item size={12} sx={{ position: 'sticky', zIndex: 10, background: '#f7f7f7', top: 0, mb: '0 !important' }}>
          <Grid container spacing={1}>
            <Grid item size={12}>
              {selectedCategory && (
                <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: '0.5%', justifyContent: 'space-between' }}>
                  {selectedCategory.properties.map(prop => (
                    Recipe.getScale(prop, true) !== false &&
                    <GaugeMeter2
                      key={prop.id}
                      label={propertyDescriptions[prop.parameter]?.label || prop.parameter}
                      descriptionHtml={propertyDescriptions[prop.parameter]?.desc || ''}
                      min={prop.min}
                      max={prop.max}
                      value={recipeObj.getAdjustedValue(prop)}
                      target={prop.target}
                      scaleMin={Recipe.getScale(prop, true)}
                      scaleMax={Recipe.getScale(prop, false)}
                    />
                  ))}
                </Box>
              )}
            </Grid>
            <Grid item size={12}
             sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap',
                    justifyContent: 'flex-end', alignContent: 'flex-start', background: '#eee',
                    gap: 1, textAlign: 'right', borderTop: '1px solid #e1e1e1' }}>
              <Box sx={{ width: 'auto', padding: '2px 18px' }}>
                  <Typography variant="overline" sx={{ mr: 1, fontWeight: 600, color: hardnessColor }}>{t('hardness')}</Typography>
                <span style={hardnessColor ? { color: hardnessColor, fontWeight: 700 } : {}}>
                  {hardnessCalc !== null ? Math.round(hardnessCalc) : '-'}
                </span>
              </Box>
              <Box sx={{borderLeft: '1px solid #ccc', padding: '2px 18px' }}>
                  <Typography variant="overline" sx={{ mr: 1, fontWeight: 600 }}>{t('%WF')}</Typography>
                {wfCalc !== null ? Math.round(wfCalc) : '-'}
              </Box>
              <Box sx={{borderLeft: '1px solid #ccc', padding: '2px 18px' }}>
                  <Typography variant="overline" sx={{ mr: 1, fontWeight: 600 }}>{t('FP')}</Typography>
                {fusionPoint !== null ? formatNumber(fusionPoint) : '-'}°C
              </Box>
            </Grid>
          </Grid>
        </Grid>
        <Grid item size={12}>
          <TableContainer component={Paper} sx={{ mb: 2 }}>
            <Table size="small" sx={{
              '& .MuiTableCell-root': {
                padding: '2px 6px',
                fontSize: '0.92rem'
              }
            }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 24, padding: '6px 4px' }} />
                  <TableCell align="left">{t('ingredient')}</TableCell>
                  <TableCell align="center" sx={{ width: 100 }}>{t('amount_g')}</TableCell>
                  {visibleColumns.fat && <TableCell align="center">{t('fat')}</TableCell>}
                  {visibleColumns.msnf && <TableCell align="center">{t('msnf')}</TableCell>}
                  {visibleColumns.total_solids && <TableCell align="center">{t('total_solids')}</TableCell>}
                  {visibleColumns.pac && <TableCell align="center">{t('pac')}</TableCell>}
                  {visibleColumns.pod && <TableCell align="center">{t('pod')}</TableCell>}
                  {visibleColumns.sugars && <TableCell align="center">{t('sugars')}</TableCell>}
                  {visibleColumns.lactose && <TableCell align="center">{t('lactose')}</TableCell>}
                  {visibleColumns.water && <TableCell align="center">{t('water')}</TableCell>}
                  {visibleColumns.proteins && <TableCell align="center">{t('proteins')}</TableCell>}
                  {visibleColumns.alcohol && <TableCell align="center">{t('alcohol')}</TableCell>}
                  <TableCell sx={{ width: 160, padding: '4px 8px' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                        {t('actions')}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={(e) => setColumnSettingsAnchor(e.currentTarget)}
                        title={t('column_settings')}
                      >
                        <SettingsIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {mixIngredientIndexes.map(idx => (
                  <RecipeIngredientRow
                    key={`mix-${idx}`}
                    idx={idx}
                    ing={ingredients[idx]}
                    ingredientOptions={combinedIngredientOptions}
                    handleIngredientChange={handleIngredientChange}
                    removeIngredient={removeIngredient}
                    disableDelete={ingredients.length - 1 === idx}
                    visibleColumns={visibleColumns}
                    draggable
                    onDragStart={handleDragStart(idx)}
                    onDragOver={handleDragOver(idx)}
                    onDrop={handleDrop(idx)}
                    mode="mix"
                  />
                ))}
                </TableBody>
              <TableHead>
                <TableRow sx={{ display: mixIngredientIndexes.length > 5 ? 'table-row' : 'none' }}>
                  <TableCell sx={{ width: 24, padding: '4px 4px' }} />
                  <TableCell align="left"></TableCell>
                  <TableCell align="center">{t('amount')}</TableCell>
                  {visibleColumns.fat && <TableCell align="center">{t('fat')}</TableCell>}
                  {visibleColumns.msnf && <TableCell align="center">{t('msnf')}</TableCell>}
                  {visibleColumns.total_solids && <TableCell align="center">{t('total_solids')}</TableCell>}
                  {visibleColumns.pac && <TableCell align="center">{t('pac')}</TableCell>}
                  {visibleColumns.pod && <TableCell align="center">{t('pod')}</TableCell>}
                  {visibleColumns.sugars && <TableCell align="center">{t('sugars')}</TableCell>}
                  {visibleColumns.lactose && <TableCell align="center">{t('lactose')}</TableCell>}
                  {visibleColumns.water && <TableCell align="center">{t('water')}</TableCell>}
                  {visibleColumns.proteins && <TableCell align="center">{t('proteins')}</TableCell>}
                  {visibleColumns.alcohol && <TableCell align="center">{t('alcohol')}</TableCell>}
                  <TableCell sx={{ width: 160, padding: '4px 8px' }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {/* Fila de totales */}
                <TableRow sx={{ background: '#f5f5f5', fontWeight: 700 }}>
                  <TableCell />
                  <TableCell align="left">{t('total')}</TableCell>
                  <TableCell align="center">
                    {recipeObj.getWeightHuman(mixWeight)}
                  </TableCell>
                  {visibleColumns.fat && (
                    <TableCell align="center">
                      {formatNumber(recipeObj.getCurrentValue('fat', false))}
                    </TableCell>
                  )}
                  {visibleColumns.msnf && (
                    <TableCell align="center">
                      {formatNumber(recipeObj.getCurrentValue('msnf', false))}
                    </TableCell>
                  )}
                  {visibleColumns.total_solids && (
                    <TableCell align="center">
                      {formatNumber(recipeObj.getCurrentValue('total_solids', false))}
                    </TableCell>
                  )}
                  {visibleColumns.pac && (
                    <TableCell align="center">
                      {formatNumber(recipeObj.getCurrentValue('pac', false))}
                    </TableCell>
                  )}
                  {visibleColumns.pod && (
                    <TableCell align="center">
                      {formatNumber(recipeObj.getCurrentValue('pod', false))}
                    </TableCell>
                  )}
                  {visibleColumns.sugars && (
                    <TableCell align="center">
                      {formatNumber(recipeObj.getCurrentValue('sugars', false))}
                    </TableCell>
                  )}
                  {visibleColumns.lactose && (
                    <TableCell align="center">
                      {formatNumber(recipeObj.getCurrentValue('lactose', false))}
                    </TableCell>
                  )}
                  {visibleColumns.water && (
                    <TableCell align="center">
                      {formatNumber(recipeObj.getCurrentValue('water', false))}
                    </TableCell>
                  )}
                  {visibleColumns.proteins && (
                    <TableCell align="center">
                      {formatNumber(recipeObj.getCurrentValue('proteins', false))}
                    </TableCell>
                  )}
                  {visibleColumns.alcohol && (
                    <TableCell align="center">
                      {formatNumber(recipeObj.getCurrentValue('alcohol', false))}
                    </TableCell>
                  )}
                  <TableCell />
                </TableRow>
                <TableRow sx={{ background: '#f5f5f5', fontWeight: 700 }}>
                  <TableCell />
                  <TableCell align="left">{t('total_percent')}</TableCell>
                  <TableCell align="right"></TableCell>
                  {visibleColumns.fat && (
                    <TableCell align="center">
                      {formatPercentage(recipeObj.getCurrentValue('fat'))}%
                    </TableCell>
                  )}
                  {visibleColumns.msnf && (
                    <TableCell align="center">
                      {formatPercentage(recipeObj.getCurrentValue('msnf'))}%
                    </TableCell>
                  )}
                  {visibleColumns.total_solids && (
                    <TableCell align="center">
                      {formatPercentage(recipeObj.getCurrentValue('total_solids'))}%
                    </TableCell>
                  )}
                  {visibleColumns.pac && (
                    <TableCell align="center">
                      PAC: {formatNumber(recipeObj.getCurrentValue('pac'))} <br />
                      PACN: {formatNumber(recipeObj.getCurrentValue('pacn'))}
                    </TableCell>
                  )}
                  {visibleColumns.pod && (
                    <TableCell align="center">
                      {formatNumber(recipeObj.getCurrentValue('pod'))}
                    </TableCell>
                  )}
                  {visibleColumns.sugars && (
                    <TableCell align="center">
                      {formatPercentage(recipeObj.getCurrentValue('sugars'))}%
                    </TableCell>
                  )}
                  {visibleColumns.lactose && (
                    <TableCell align="center">
                      {formatPercentage(recipeObj.getCurrentValue('lactose'))}%
                    </TableCell>
                  )}
                  {visibleColumns.water && (
                    <TableCell align="center">
                      {formatPercentage(recipeObj.getCurrentValue('water'))}%
                    </TableCell>
                  )}
                  {visibleColumns.proteins && (
                    <TableCell align="center">
                      {formatPercentage(recipeObj.getCurrentValue('proteins'))}%
                    </TableCell>
                  )}
                  {visibleColumns.alcohol && (
                    <TableCell align="center">
                      {formatPercentage(recipeObj.getCurrentValue('alcohol'))}%
                    </TableCell>
                  )}
                  <TableCell />
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <Paper sx={{ p: 2, mb: 2, backgroundColor: 'rgba(25,118,210,0.06)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <InclusionIcon sx={{ fontSize: 20, color: 'secondary.main' }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {t('inclusion_ingredients')}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('inclusion_ingredients_help')}
            </Typography>
            <TableContainer component={Box} sx={{ mb: 2, border: '1px solid rgba(25,118,210,0.2)', borderRadius: 1, overflowX: 'auto' }}>
              <Table size="small" sx={{ '& .MuiTableCell-root': { padding: '2px 6px', fontSize: '0.92rem' } }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 24 }} />
                    <TableCell align="left">{t('ingredient')}</TableCell>
                    <TableCell align="center" sx={{ width: 100 }}>{t('amount_g')}</TableCell>
                    {visibleColumns.fat && <TableCell align="center">{t('fat')}</TableCell>}
                    {visibleColumns.msnf && <TableCell align="center">{t('msnf')}</TableCell>}
                    {visibleColumns.total_solids && <TableCell align="center">{t('total_solids')}</TableCell>}
                    {visibleColumns.pac && <TableCell align="center">{t('pac')}</TableCell>}
                    {visibleColumns.pod && <TableCell align="center">{t('pod')}</TableCell>}
                    {visibleColumns.sugars && <TableCell align="center">{t('sugars')}</TableCell>}
                    {visibleColumns.lactose && <TableCell align="center">{t('lactose')}</TableCell>}
                    {visibleColumns.water && <TableCell align="center">{t('water')}</TableCell>}
                    {visibleColumns.proteins && <TableCell align="center">{t('proteins')}</TableCell>}
                    {visibleColumns.alcohol && <TableCell align="center">{t('alcohol')}</TableCell>}
                    <TableCell sx={{ width: 160, padding: '4px 8px' }} align="right">
                      <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                        {t('actions')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {nonMixIngredientIndexes.length > 0 ? (
                    nonMixIngredientIndexes.map(idx => (
                      <RecipeIngredientRow
                        key={`inclusion-${idx}`}
                        idx={idx}
                        ing={ingredients[idx]}
                        ingredientOptions={combinedIngredientOptions}
                        handleIngredientChange={handleIngredientChange}
                        removeIngredient={removeIngredient}
                        disableDelete={false}
                        visibleColumns={visibleColumns}
                        draggable
                        onDragStart={handleDragStart(idx)}
                        onDragOver={handleDragOver(idx)}
                        onDrop={handleDrop(idx)}
                        mode="inclusion"
                      />
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={tableColumnSpan} align="center" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
                        {t('no_inclusion_ingredients')}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Typography variant="body2">
                <strong>{t('mix_weight')}:</strong> {recipeObj.getWeightHuman(mixWeight)}
              </Typography>
              <Typography variant="body2">
                <strong>{t('inclusion_weight')}:</strong> {recipeObj.getWeightHuman(inclusionWeight)}
              </Typography>
              <Typography variant="body2">
                <strong>{t('total_weight')}:</strong> {recipeObj.getWeightHuman(recipeObj.getTotalWeight())}
              </Typography>
            </Box>
          </Paper>
        </Grid>
        <Grid item size={12}>
          <Paper sx={{ p: 2, mb: 2, backgroundColor: 'rgba(25,118,210,0.06)' }}>
            <Typography gutterBottom>{t('evaporation_percentage')} ({evaporationPercentage}%)</Typography>
            <Slider min={0} max={70} value={evaporationPercentage}
              onChange={(_, v) => setEvaporationPercentage(v)}
              valueLabelDisplay="auto"
              step={0.5} />
            {evaporationPercentage > 0 && recipeObj.getCurrentValue('alcohol') > 0 && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={includeAlcoholInEvaporation}
                    onChange={(e) => setIncludeAlcoholInEvaporation(e.target.checked)}
                    size="small"
                  />
                }
                label={<Typography variant="caption">{t('include_alcohol_in_evaporation')}</Typography>}
                sx={{ mt: 0.5 }}
              />
            )}
            {evaporationPercentage > 0 && (
              <Grid item size={12}>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                  {t('adjusted_values')} ({t('evaporation_percentage')}: {evaporationPercentage}%)
                  {recipeObj.getCurrentValue('alcohol') > 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 400, fontSize: '0.875rem' }}>
                      {includeAlcoholInEvaporation 
                        ? t('alcohol') + ': ' + t('included_in_process')
                        : t('alcohol') + ': ' + t('added_after_processing')
                      }
                    </Typography>
                  )}
                  <IconButton
                    size="small"
                    onClick={(e) => setAdjustedPropsSettingsAnchor(e.currentTarget)}
                    title={t('column_settings')}
                    sx={{ ml: 1 }}
                  >
                    <SettingsIcon fontSize="small" />
                  </IconButton>
                </Typography>
                <TableContainer component={Paper} sx={{ mb: 2, background: '#f9f9f9' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>{t('component')}</strong></TableCell>
                        <TableCell align="center"><strong>{t('original')}</strong></TableCell>
                        <TableCell align="center"><strong>{t('evaporated')}</strong></TableCell>
                        <TableCell align="center"><strong>{t('adjusted')}</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>{t('water')}</TableCell>
                        <TableCell align="center">{formatPercentage(recipeObj.getCurrentValue('water'))}%</TableCell>
                        <TableCell align="center">{formatNumber(recipeObj.getEvaporatedWaterAmount())}</TableCell>
                        <TableCell align="center">{formatPercentage(recipeObj.getAdjustedValue('water'))}%</TableCell>
                      </TableRow>
                      {recipeObj.getCurrentValue('alcohol') > 0 && (
                        <TableRow>
                          <TableCell>{t('alcohol')}</TableCell>
                          <TableCell align="center">{formatPercentage(recipeObj.getCurrentValue('alcohol'))}%</TableCell>
                          <TableCell align="center">{formatNumber(recipeObj.getEvaporatedAlcoholAmount())}</TableCell>
                          <TableCell align="center">{formatPercentage(recipeObj.getAdjustedValue('alcohol'))}%</TableCell>
                        </TableRow>
                      )}
                      {visibleAdjustedProps.total_solids && (
                        <TableRow>
                          <TableCell>{t('total_solids')}</TableCell>
                          <TableCell align="center">{formatPercentage(recipeObj.getCurrentValue('total_solids'))}%</TableCell>
                          <TableCell align="center">-</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 600 }}>{formatPercentage(recipeObj.getAdjustedValue('total_solids'))}%</TableCell>
                        </TableRow>
                      )}
                      {visibleAdjustedProps.pac && (
                        <TableRow>
                          <TableCell>{t('pac')}</TableCell>
                          <TableCell align="center">{formatNumber(recipeObj.getCurrentValue('pac'))}</TableCell>
                          <TableCell align="center">
                            {recipeObj.getCurrentValue('alcohol') > 0 && includeAlcoholInEvaporation 
                              ? `-${formatNumber(recipeObj.getPacLostFromAlcohol())}`
                              : '-'
                            }
                          </TableCell>
                          <TableCell align="center" sx={{ fontWeight: 600 }}>{formatNumber(recipeObj.getAdjustedValue('pac'))}</TableCell>
                        </TableRow>
                      )}
                      {visibleAdjustedProps.fat && (
                        <TableRow>
                          <TableCell>{t('fat')}</TableCell>
                          <TableCell align="center">{formatPercentage(recipeObj.getCurrentValue('fat'))}%</TableCell>
                          <TableCell align="center">-</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 600 }}>{formatPercentage(recipeObj.getAdjustedValue('fat'))}%</TableCell>
                        </TableRow>
                      )}
                      {visibleAdjustedProps.msnf && (
                        <TableRow>
                          <TableCell>{t('msnf')}</TableCell>
                          <TableCell align="center">{formatPercentage(recipeObj.getCurrentValue('msnf'))}%</TableCell>
                          <TableCell align="center">-</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 600 }}>{formatPercentage(recipeObj.getAdjustedValue('msnf'))}%</TableCell>
                        </TableRow>
                      )}
                      {visibleAdjustedProps.sugars && (
                        <TableRow>
                          <TableCell>{t('sugars')}</TableCell>
                          <TableCell align="center">{formatPercentage(recipeObj.getCurrentValue('sugars'))}%</TableCell>
                          <TableCell align="center">-</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 600 }}>{formatPercentage(recipeObj.getAdjustedValue('sugars'))}%</TableCell>
                        </TableRow>
                      )}
                      {visibleAdjustedProps.proteins && (
                        <TableRow>
                          <TableCell>{t('proteins')}</TableCell>
                          <TableCell align="center">{formatPercentage(recipeObj.getCurrentValue('proteins'))}%</TableCell>
                          <TableCell align="center">-</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 600 }}>{formatPercentage(recipeObj.getAdjustedValue('proteins'))}%</TableCell>
                        </TableRow>
                      )}
                      {visibleAdjustedProps.lactose && (
                        <TableRow>
                          <TableCell>{t('lactose')}</TableCell>
                          <TableCell align="center">{formatPercentage(recipeObj.getCurrentValue('lactose'))}%</TableCell>
                          <TableCell align="center">-</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 600 }}>{formatPercentage(recipeObj.getAdjustedValue('lactose'))}%</TableCell>
                        </TableRow>
                      )}
                      {visibleAdjustedProps.pod && (
                        <TableRow>
                          <TableCell>{t('pod')}</TableCell>
                          <TableCell align="center">{formatNumber(recipeObj.getCurrentValue('pod'))}</TableCell>
                          <TableCell align="center">-</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 600 }}>{formatNumber(recipeObj.getAdjustedValue('pod'))}</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            )}
          </Paper>
        </Grid>
        <Grid item size={12}>
          <Box sx={{ width: '100%', height: 220, mb: 2, mt: 0 }}>
            <FreezingCurve recipeObj={recipeObj} servingTemp={servingTemp} />
          </Box>
        </Grid>
        <Grid item size={12}>
          <TextField
            label={t('notes')}
            value={notes}
            onChange={e => setNotes(e.target.value)}
            multiline
            rows={3}
            fullWidth
          />
        </Grid>
        <Grid item size={12}  sx={{ display: 'flex', justifyContent: 'start', gap: 1, mt: 2, flexWrap: 'wrap' }}>
          <TextField
            select
            label={t('status')}
            value={status}
            onChange={e => setStatus(e.target.value)}
            size="small"
            sx={{ minWidth: 120 }}
          >
            {statusOptions.map(opt => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </TextField>
          <Button type="submit" variant="contained" color="primary">
            {initialRecipe && initialRecipe.id ? t('update_recipe') : t('save_recipe')}
          </Button>
          <IconButton
            onClick={(e) => setMenuAnchor(e.currentTarget)}
            size="large"
            sx={{ ml: 1 }}
          >
            <MoreVertIcon />
          </IconButton>
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
          >
            <MenuItem 
              onClick={() => { setTranslationsDialogOpen(true); setMenuAnchor(null); }}
              disabled={!editingId}
            >
              <TranslateIcon sx={{ mr: 1 }} /> {t('translations') || 'Translations...'}
            </MenuItem>
            <MenuItem onClick={() => { handleExport(); setMenuAnchor(null); }}>
              <FileDownloadIcon sx={{ mr: 1 }} /> {t('export_recipe')}
            </MenuItem>
            <MenuItem component="label">
              <FileUploadIcon sx={{ mr: 1 }} /> {t('import_recipe')}
              <input
                type="file"
                accept="application/json"
                hidden
                ref={fileInputRef}
                onChange={(e) => { handleImport(e); setMenuAnchor(null); }}
              />
            </MenuItem>
            <MenuItem 
              onClick={() => { handleCreateIngredient(); setMenuAnchor(null); }}
              disabled={!name || ingredients.length === 0}
            >
              {t('save_as_ingredient')}
            </MenuItem>
          </Menu>
        </Grid>
      </Grid>        
      {importWarnings.length > 0 && (
        <Box sx={{ mb: 2, mt: 2, p: 2, background: '#fffbe6', border: '1px solid #ffe082', borderRadius: 1 }}>
          <Typography variant="subtitle2" color="warning.main" sx={{ mb: 1 }}>
            {t('ingredient_properties_changed') || 'Some ingredient properties have changed:'}
          </Typography>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {importWarnings.map((w, i) => (
              <li key={i} style={{ color: '#b26a00', fontSize: 14 }}>{w}</li>
            ))}
          </ul>
        </Box>
      )}
      <Grid item size={12} sx={{ mt: 4 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          {t('scaled_ingredients')}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, mt: 4, gap: 1 }}>
          <TextField
            label={t('weight') || 'Weight'}
            type="number"
            size="small"
            value={copyGrams}
            onChange={e => setCopyGrams(Number(e.target.value))}
            sx={{ width: 120 }}
            inputProps={{ min: 1 }}
          />
          <Button
            variant="outlined"
            startIcon={<ContentCopyIcon />}
            onClick={() => {
              const scaled = recipeObj.getScaledIngredientsText(copyGrams);
              if (!scaled) return;
              navigator.clipboard.writeText(scaled);
            }}
            disabled={recipeObj.getTotalWeight() === 0}
          >
            {t('copy_scaled_ingredients')}
          </Button>
        </Box>
        {/* Tabla de ingredientes escalados */}
        <TableContainer component={Paper} sx={{ mt: 1, mb: 2, background: '#f7f7f7' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('ingredient')}</TableCell>
                <TableCell align="right">{t('amount_g')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {recipeObj.getScaledIngredientsText(copyGrams)
                ? recipeObj.getScaledIngredientsText(copyGrams)
                    .split('\n')
                    .map((line, idx) => {
                      const [name, amountRaw] = line.split(':').map(s => s.trim());
                      let amount = amountRaw;
                      if (amountRaw && amountRaw.endsWith('g')) {
                        let num = parseFloat(amountRaw.replace('g', '').trim());
                        if (!isNaN(num)) {
                          amount = formatNumber(num) + ' g';
                        }
                      }
                      return (
                        <TableRow key={idx}>
                          <TableCell>{name}</TableCell>
                          <TableCell align="right">{amount}</TableCell>
                        </TableRow>
                      );
                    })
                : (
                  <TableRow>
                    <TableCell colSpan={2} sx={{ color: '#aaa', textAlign: 'center' }}>
                      {t('no_ingredients')}
                    </TableCell>
                  </TableRow>
                )
              }
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
      
      {/* Dialog para crear ingrediente */}
      <IngredientForm
        open={showIngredientDialog}
        onClose={handleIngredientDialogClose}
        onSubmit={handleIngredientSaved}
        ingredient={newIngredientData}
        api={api}
      />

      {/* Popover para configuración de columnas */}
      <Popover
        open={Boolean(columnSettingsAnchor)}
        anchorEl={columnSettingsAnchor}
        onClose={() => setColumnSettingsAnchor(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Box sx={{ p: 2, minWidth: 200 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            {t('visible_columns') || 'Columnas visibles'}
          </Typography>
          <FormGroup>
            {availableColumns.map(col => (
              <FormControlLabel
                key={col.key}
                control={
                  <Checkbox
                    checked={visibleColumns[col.key]}
                    onChange={() => handleColumnToggle(col.key)}
                    size="small"
                  />
                }
                label={col.label}
              />
            ))}
          </FormGroup>
        </Box>
      </Popover>

      {/* Popover para configuración de propiedades ajustadas */}
      <Popover
        open={Boolean(adjustedPropsSettingsAnchor)}
        anchorEl={adjustedPropsSettingsAnchor}
        onClose={() => setAdjustedPropsSettingsAnchor(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Box sx={{ p: 2, minWidth: 200 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            {t('visible_properties') || 'Propiedades visibles'}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            {t('water_alcohol_always_visible') || 'Agua y alcohol siempre visibles'}
          </Typography>
          <FormGroup>
            {availableAdjustedProps.map(prop => (
              <FormControlLabel
                key={prop.key}
                control={
                  <Checkbox
                    checked={visibleAdjustedProps[prop.key]}
                    onChange={() => handleAdjustedPropToggle(prop.key)}
                    size="small"
                  />
                }
                label={prop.label}
              />
            ))}
          </FormGroup>
        </Box>
      </Popover>

      {/* Dialog de traducciones */}
      <RecipeTranslationsDialog
        open={translationsDialogOpen}
        onClose={(saved) => {
          setTranslationsDialogOpen(false);
          if (saved) {
            // Opcionalmente recargar la receta para ver las traducciones actualizadas
            console.log('Translations saved');
          }
        }}
        recipeId={editingId}
        recipeName={name}
        recipeNotes={notes}
        api={api}
      />
    </form>
  );
};

export default RecipeEditor;